import type { ReactNode } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { DocumentViewerFlyout } from "~/shared/components/document-viewer-flyout/document-viewer-flyout.tsx";
import { DocumentViewerFlyoutMobile } from "~/shared/components/document-viewer-flyout/document-viewer-flyout-mobile.tsx";
import { useDocumentViewerSelection } from "~/shared/components/document-viewer-flyout/hooks/use-document-viewer-selection.ts";
import { useGetInvoiceDocuments } from "~/shared/hooks/documents/use-get-invoice-documents.ts";
import {
	fetchDocumentByPoAndFileName,
	useGetDocumentByPoAndFileName,
} from "~/shared/hooks/documents/use-get-selected-document.ts";
import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import { useIsMobile } from "~/shared/hooks/use-is-mobile.ts";
import type { DefaultDocument } from "~/shared/types/payment-types";
import { mergePdfs, printPdf } from "~/shared/utils/print-utils.ts";

const LTL_SHIPPING_LABEL_DOCUMENT_ID = 319;

type DocumentViewerProps = {
	poNumber: number | undefined;
	viewerState: {
		isOpen: boolean;
		selectedDocument: DefaultDocument | null;
	};
	onClose: () => void;
	onSelectDocument: (doc: DefaultDocument) => void;
	title?: string;
	/** Additional feature-specific buttons rendered after Download/Print (e.g. shipment edit's document generation action). */
	extraHeaderActions?: ReactNode;
	/** Forces a fresh fetch of the document list/content on every open instead of serving cached data (e.g. edit shipment, where documents may have just been generated). */
	refetchOnOpen?: boolean;
};

export function DocumentViewer({
	poNumber,
	viewerState,
	onClose,
	onSelectDocument,
	title = "Shipment Documents",
	extraHeaderActions,
	refetchOnOpen = false,
}: Readonly<DocumentViewerProps>) {
	const [isDownloading, setIsDownloading] = useState(false);
	const [isPrinting, setIsPrinting] = useState(false);
	// The buttons only disable on the next commit, so a repeat activation before
	// then would otherwise start a second fetch-and-merge run.
	const isPrintingRef = useRef(false);
	const isDownloadingRef = useRef(false);
	const { isInternal } = useDeploymentType();
	const isMobile = useIsMobile();

	const {
		documents: rawDocuments,
		isLoading: isLoadingDocuments,
		refetch: refetchDocuments,
	} = useGetInvoiceDocuments(poNumber, viewerState.isOpen, isInternal);

	// For LTL shipping labels (document ID 319), only show the most recent one
	const documents = useMemo(() => {
		const shippingLabels = rawDocuments.filter(
			(doc) => doc.documentID === LTL_SHIPPING_LABEL_DOCUMENT_ID,
		);
		if (shippingLabels.length <= 1) return rawDocuments;

		const mostRecent = shippingLabels.at(-1);
		return rawDocuments.filter(
			(doc) =>
				doc.documentID !== LTL_SHIPPING_LABEL_DOCUMENT_ID || doc === mostRecent,
		);
	}, [rawDocuments]);

	const {
		document: selectedDocData,
		isLoading: isLoadingInvoiceDocument,
		refetch: refetchSelectedDocument,
	} = useGetDocumentByPoAndFileName(
		poNumber,
		documents?.find(
			(doc) =>
				doc.documentFileName === viewerState.selectedDocument?.documentFileName,
		)?.documentFileName,
		viewerState.isOpen,
		isInternal,
	);

	const selectedFileName = viewerState.selectedDocument?.documentFileName;

	// TanStack Query won't auto-refetch already-cached data just because `enabled`
	// flips back to true, so force it here for consumers that need always-fresh docs.
	useEffect(() => {
		if (refetchOnOpen && viewerState.isOpen) {
			void refetchDocuments();
		}
	}, [refetchOnOpen, viewerState.isOpen, refetchDocuments]);

	useEffect(() => {
		if (refetchOnOpen && viewerState.isOpen && selectedFileName) {
			void refetchSelectedDocument();
		}
	}, [
		refetchOnOpen,
		viewerState.isOpen,
		selectedFileName,
		refetchSelectedDocument,
	]);

	const [base64Data, setBase64Data] = useState<string | null>(null);

	useEffect(() => {
		if (selectedDocData) {
			// Convert ArrayBuffer to base64 data URL for PDFViewer
			const uint8Array = new Uint8Array(selectedDocData);
			let binaryString = "";
			for (const element of uint8Array) {
				binaryString += String.fromCodePoint(element);
			}
			const base64 = btoa(binaryString);
			const dataUrl = `data:application/pdf;base64,${base64}`;
			setBase64Data(dataUrl);

			return () => {
				setBase64Data(null);
			};
		}
		return undefined;
	}, [selectedDocData]);

	const handlePrint = useCallback(
		async (docFileNames: string[]) => {
			if (!poNumber || !documents) return;
			if (isPrintingRef.current) return;

			isPrintingRef.current = true;
			setIsPrinting(true);
			try {
				const binaryDocs: ArrayBuffer[] = [];

				for (const fileName of docFileNames) {
					const docMetadata = documents.find(
						(d) => d.documentFileName === fileName,
					);
					if (docMetadata) {
						try {
							const binary = await fetchDocumentByPoAndFileName(
								poNumber,
								docMetadata.documentFileName,
								isInternal,
							);
							if (binary) {
								binaryDocs.push(binary);
							}
						} catch (error) {
							console.error(`Failed to fetch ${fileName}:`, error);
						}
					}
				}

				if (binaryDocs.length > 0) {
					try {
						const mergedPdf = await mergePdfs(binaryDocs);
						printPdf(mergedPdf);
					} catch (error) {
						console.error("Failed to merge and print PDFs:", error);
					}
				}
			} finally {
				isPrintingRef.current = false;
				setIsPrinting(false);
			}
		},
		[poNumber, documents, isInternal],
	);

	const handleDownload = useCallback(
		async (docFileNames: string[]) => {
			if (!poNumber || !documents) return;
			if (isDownloadingRef.current) return;

			isDownloadingRef.current = true;
			setIsDownloading(true);
			try {
				for (const fileName of docFileNames) {
					const docMetadata = documents.find(
						(d) => d.documentFileName === fileName,
					);
					if (!docMetadata) continue;

					try {
						const binary = await fetchDocumentByPoAndFileName(
							poNumber,
							docMetadata.documentFileName,
							isInternal,
						);
						if (!binary) continue;

						const blob = new Blob([binary as BlobPart], {
							type: "application/pdf",
						});
						const url = URL.createObjectURL(blob);

						const downloadLink = globalThis.document.createElement("a");
						downloadLink.href = url;
						downloadLink.download = docMetadata.documentFileName;
						document.body.append(downloadLink);
						downloadLink.click();
						downloadLink.remove();
						URL.revokeObjectURL(url);
					} catch (error) {
						console.error(`Failed to fetch ${fileName} for download:`, error);
					}
				}
			} finally {
				isDownloadingRef.current = false;
				setIsDownloading(false);
			}
		},
		[poNumber, documents, isInternal],
	);

	const mappedDocuments = useMemo(
		() =>
			documents?.map((doc) => ({
				documentID: doc.documentID,
				documentDescription: doc.documentDescription,
				documentFileName: doc.documentFileName,
				categoryDocumentID: doc.categoryDocumentID,
			})) ?? [],
		[documents],
	);

	const {
		itemRenderer,
		handlePrintClick,
		handleDownloadClick,
		isNoneSelected,
	} = useDocumentViewerSelection({
		viewerState,
		documents: mappedDocuments,
		selectDocument: onSelectDocument,
		onPrint: (docs) => {
			void handlePrint(docs);
		},
		onDownload: (docs) => {
			void handleDownload(docs);
		},
	});

	const flyoutProps = {
		closeViewer: onClose,
		contentData: base64Data ?? "",
		documents: mappedDocuments,
		extraHeaderActions,
		handleDownloadClick,
		handlePrintClick,
		isDownloading,
		isLoadingDocumentList: isLoadingDocuments,
		isLoadingInvoiceDocument,
		isMobile,
		isNoneSelected,
		isPrinting,
		itemRenderer,
		title,
		viewerState,
	};

	return isMobile ? (
		<DocumentViewerFlyoutMobile {...flyoutProps} />
	) : (
		<DocumentViewerFlyout {...flyoutProps} />
	);
}
